import { type VoidComponent, createMemo, Show } from "solid-js";
import { array, instance, literal, nonEmpty, object, pipe, union } from "valibot";
import { isNil } from "lodash-es";
import InputModal from "../../components/modal/InputModal";
import { MultiSelectWithSearchInput } from "../../components/menu/MultiSelectWithSearch";
import { SingleSelectInput } from "../../components/menu/Select";
import UserService from "../../services/UserService";
import { useCustomerState } from "../../state/customer";
import { useTranslation } from "../../state/i18n";
import { useModalManager } from "../../components/modal/ModalManager";
import GenericFailureModal from "../GenericFailureModal";
import { roleReference } from "../../utils/validation/idReferences";
import { filterMixedRolesForScope, groupRoles, toGroupedRoleOptions } from "../../utils/roleUtil";
import { resolveName as resolveGroupName } from "../../utils/groupUtil";
import { groupAvatar, toSelectOption } from "../../utils/avatarUtil";
import type { Grant } from "../../state/grant";
import type RoleId from "../../state/RoleId";
import UserId from "../../state/UserId";
import InviteId from "../../state/InviteId";
import GroupId from "../../state/GroupId";
import type FolderId from "../../state/FolderId";
import type FormId from "../../state/FormId";
import type { SelectOption } from "../../types/Menu";
import type { MoreAppError } from "../../MoreAppError";

type AddPermissionModalProps = (
  | { resourceType: "FOLDER"; resourceId: FolderId }
  | { resourceType: "FORM"; resourceId: FormId }
) & {
  onSuccess?: () => void;
};

type Member = { kind: "user"; id: UserId } | { kind: "invite"; id: InviteId } | { kind: "group"; id: GroupId };

const memberSchema = union([
  object({ kind: literal("user"), id: instance(UserId) }),
  object({ kind: literal("invite"), id: instance(InviteId) }),
  object({ kind: literal("group"), id: instance(GroupId) }),
]);

const addPermissionSchema = () =>
  object({
    members: pipe(array(memberSchema), nonEmpty()),
    roleId: roleReference(),
  });

export const AddPermissionModal: VoidComponent<AddPermissionModalProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { replace } = useModalManager();

  const usersState = UserService.getUsers(customer.customerId);
  const invitesState = UserService.getInvites(customer.customerId);
  const groupsState = UserService.getGroups(customer.customerId);
  const rolesState = UserService.getRoles(customer.customerId);

  const updateGrantMutation = UserService.updateGrant(customer);
  const updateGroupGrantMutation = UserService.updateGroupGrant(customer);

  const grantResource = () =>
    props.resourceType === "FORM"
      ? ({ resourceType: "FORM", resourceId: props.resourceId } as const)
      : ({ resourceType: "FOLDER", resourceId: props.resourceId } as const);

  const resolvedState = createMemo(() => {
    if (isNil(usersState.data) || isNil(invitesState.data) || isNil(groupsState.data) || isNil(rolesState.data)) {
      return;
    }
    return {
      users: usersState.data,
      invites: invitesState.data,
      groups: groupsState.data,
      roles: rolesState.data,
    };
  });

  const hasGrant = (grants: Grant[]) =>
    grants.some(
      (grant) =>
        grant.resourceType === props.resourceType && grant.resourceId.toString() === props.resourceId.toString(),
    );

  const roleOptions = createMemo<SelectOption<RoleId>[]>(() => {
    const resolved = resolvedState();
    if (isNil(resolved)) {
      return [];
    }

    const grouped = groupRoles(resolved.roles);
    const scopeRoles = props.resourceType === "FORM" ? grouped.formRoles : grouped.folderRoles;
    const roles = [...scopeRoles, ...filterMixedRolesForScope(resolved.roles, props.resourceType)];

    return toGroupedRoleOptions(
      roles,
      t,
      t(($) => $.ADD_PERMISSION_MODAL_CUSTOM_ROLE_LABEL),
    );
  });

  const onConfirm = async (value: { members: Member[]; roleId: RoleId }) => {
    const resource = grantResource();
    const results = await Promise.allSettled(
      value.members.map((member) => {
        if (member.kind === "group") {
          return updateGroupGrantMutation.mutateAsync({
            ...resource,
            groupId: member.id,
            roleId: value.roleId,
            currentRoleId: null,
          });
        }
        return updateGrantMutation.mutateAsync({
          ...resource,
          userOrInviteId: member.id,
          isInvited: member.kind === "invite",
          roleId: value.roleId,
          currentRoleId: null,
        });
      }),
    );

    const failure = results.find((result) => result.status === "rejected");
    if (!isNil(failure)) {
      const error = failure.reason as MoreAppError;
      replace({
        content: () => <GenericFailureModal title={t(($) => $.ADD_PERMISSION_FAILURE_MODAL_TITLE)} error={error} />,
      });
      throw error;
    }

    props.onSuccess?.();
  };

  return (
    <InputModal
      variant="default"
      size="large"
      titleIcon="userAdd"
      title={t(($) => $.ADD_PERMISSION_MODAL_TITLE)}
      confirmLabel={t(($) => $.ADD_PERMISSION_MODAL_CONFIRM)}
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      onConfirm={onConfirm}
      initialValue={{ members: [], roleId: undefined }}
      schema={addPermissionSchema()}
    >
      {({ value, updateValue, error }) => (
        <Show when={resolvedState()}>
          {(data) => {
            const selectableUsers = [
              ...data().users.filter((user) => !hasGrant(user.grants)),
              ...data().invites.filter((invite) => !hasGrant(invite.grants)),
            ];
            const selectableGroups = data().groups.filter((group) => !hasGrant(group.grants));

            return (
              <div class="flex flex-col gap-4">
                <MultiSelectWithSearchInput<Member>
                  label={t(($) => $.ADD_PERMISSION_MODAL_MEMBERS_LABEL)}
                  placeholder={t(($) => $.ADD_PERMISSION_MODAL_MEMBERS_PLACEHOLDER)}
                  value={value("members")}
                  onSelect={(members) => updateValue("members", members)}
                  required
                  width="matchTrigger"
                  errorMessage={error("members")}
                  options={{
                    [t(($) => $.SELECT_TAB_TITLE_USERS)]: selectableUsers.map(toSelectOption),
                    [t(($) => $.SELECT_TAB_TITLE_GROUPS)]: selectableGroups.map((group) => ({
                      variant: "avatar",
                      ...groupAvatar(group),
                      label: resolveGroupName(group),
                      value: { kind: "group", id: group.id },
                    })),
                  }}
                />
                <SingleSelectInput<RoleId>
                  label={t(($) => $.FORM_PAGE_PERMISSIONS_TAB_FORM_ROLE_TITLE)}
                  placeholder={t(($) => $.ADD_PERMISSION_MODAL_ROLE_PLACEHOLDER)}
                  options={roleOptions()}
                  value={value("roleId")}
                  onSelect={(roleId) => updateValue("roleId", roleId)}
                  required
                  sameWidth
                  errorMessage={error("roleId")}
                />
              </div>
            );
          }}
        </Show>
      )}
    </InputModal>
  );
};

export default AddPermissionModal;
